import { Dimension, Position } from 'types';
import useFindNodeInCache from 'components/canvas/hooks/use-find-node';
import useNodeDimensionsOperations from 'components/canvas/hooks/use-node-dimensions-operations';
import { differenceBetweenNumbers } from 'utils/js';
import { SurfaceNodePointsTo, SurfaceNodeUnion } from 'graphql/types';
import useCanvas from 'components/canvas/hooks/use-canvas';
import { isUndefined } from 'lodash';

interface LinkConnectionOperations {
  getLinkConnectionInformation: (
    link: SurfaceNodePointsTo,
  ) => LinkConnectionInformation;
  getLinkConnectionPosition: (
    link: SurfaceNodePointsTo,
    offset?: number,
  ) => { start: Position; end: Position } | undefined | {};
  calculateLinkConnectionPosition: (nodeId: string) => Position | undefined;
}

interface LinkConnectionInformation {
  isAbove: boolean;
  isBelow: boolean;
  isPointingLeft: boolean;
  isPointingRight: boolean;
  endNode: SurfaceNodeUnion;
  startNode: SurfaceNodeUnion;
  startNodeDimensions: Dimension;
  endNodeDimensions: Dimension;
  areConnectionsVerticallyAligned: boolean;
  areConnectionsHorizontallyAligned: boolean;
}

export default function useLinkConnectionOperations(
  surfaceId: string,
): LinkConnectionOperations {
  const { findNodeFromLink, findNodeById } = useFindNodeInCache(surfaceId);

  const { getNodeDimensions } = useNodeDimensionsOperations();

  const { findShapeById } = useCanvas();

  function getNodeShapePosition(id: string) {
    const nodeShape = findShapeById(id);

    let position = nodeShape?.position();

    if (isUndefined(nodeShape)) {
      position = findNodeById(id)?.position;
    }

    return position || { x: 0, y: 0 };
  }

  function getLinkConnectionInformation(
    link: SurfaceNodePointsTo,
  ): LinkConnectionInformation {
    const endNode = findNodeFromLink(link.to);
    const startNode = findNodeFromLink(link.from);

    const endNodeDimensions = getNodeDimensions(endNode);
    const startNodeDimensions = getNodeDimensions(startNode);

    const startPosition = getNodeShapePosition(startNode.id);
    const endPosition = getNodeShapePosition(endNode.id);

    const isAbove =
      startPosition.y + startNodeDimensions.height < endPosition.y;

    const isBelow = startPosition.y > endPosition.y + endNodeDimensions.height;

    const isPointingLeft = startPosition.x < endPosition.x;
    const isPointingRight = startPosition.x > endPosition.x;

    // To some extent
    const areConnectionsHorizontallyAligned =
      differenceBetweenNumbers(startPosition.y, endPosition.y) < 200;

    const areConnectionsVerticallyAligned =
      differenceBetweenNumbers(startPosition.x, endPosition.x) < 200;

    return {
      isAbove,
      isBelow,
      isPointingLeft,
      isPointingRight,
      endNode,
      startNode,
      endNodeDimensions,
      startNodeDimensions,
      areConnectionsVerticallyAligned,
      areConnectionsHorizontallyAligned,
    };
  }

  function getLinkConnectionPosition(
    link,
    offset = 0,
  ): { start: Position; end: Position } | undefined {
    const start = calculateLinkConnectionPosition(link.from.id);

    const end = calculateLinkConnectionPosition(link.to.id);

    if (start && end) {
      const {
        isAbove,
        isBelow,
        isPointingLeft,
        isPointingRight,
        endNodeDimensions,
        startNodeDimensions,
      } = getLinkConnectionInformation(link);

      // Link is pointing to the right
      if (isPointingLeft && !isBelow && !isAbove) {
        end.x = end.x - endNodeDimensions.width / 2 - offset;
        start.x = start.x + startNodeDimensions.width / 2 + offset;
      }

      // Link is pointing on the left
      if (isPointingRight && !isBelow && !isAbove) {
        end.x = end.x + endNodeDimensions.width / 2 + offset;
        start.x = start.x - startNodeDimensions.width / 2 - offset;
      }

      if (isBelow) {
        start.y = start.y - startNodeDimensions.height / 2 - offset;
        end.y = end.y + endNodeDimensions.height / 2 + offset;
      }

      if (isAbove) {
        start.y = start.y + startNodeDimensions.height / 2 + offset;
        end.y = end.y - endNodeDimensions.height / 2 - offset;
      }

      return { start, end };
    }

    // @ts-ignore
    return { start: undefined, end: undefined };
  }

  function calculateLinkConnectionPosition(
    nodeId: string,
  ): Position | undefined {
    const { x, y } = getNodeShapePosition(nodeId);

    const node = findNodeById(nodeId);

    if (node) {
      const { width, height } = getNodeDimensions(node);

      return {
        x: x + width / 2,
        y: y + height / 2,
      };
    }
  }

  return {
    calculateLinkConnectionPosition,
    getLinkConnectionInformation,
    getLinkConnectionPosition,
  };
}
